import { useMemo } from "react";
import dayjs from "dayjs";

import {
  useGetTaskBoardActivitySourceQuery,
  type ActivitySourceProject,
} from "../../../../app/services/taskBoard";
import {
  collectTaskActivities,
  type ActivityEntry,
} from "./taskBoardActivity";

export interface ActivityEntriesResult {
  /** 全プロジェクト・全ボード・全タスクを走査して平坦化したアクティビティ */
  entries: ActivityEntry[];
  /** 元データのプロジェクト一覧（バナー配色・遷移先の解決に使う） */
  projects: ActivitySourceProject[];
  isLoading: boolean;
  error: unknown;
}

/**
 * アクティビティ集計の元データ（`GET /api/task-boards/activity-source`）を
 * 取得し、`@山田[3h]` 記法を {@link ActivityEntry} の配列へ平坦化するフック。
 *
 * アクティビティ集計ビュー（ActivityMonitorView）・配色（useActivityColors）・
 * 期間フィルターの月チップ母集合（CrossProjectTaskBoard）で共用する。
 * RTK Query が同一エンドポイントの購読をまとめるため、併用しても
 * リクエストは 1 回で済む。
 */
export function useActivityEntries(): ActivityEntriesResult {
  const { data, isLoading, error } = useGetTaskBoardActivitySourceQuery();

  const projects = useMemo(() => data?.data?.projects || [], [data]);

  const entries = useMemo<ActivityEntry[]>(() => {
    const base = dayjs();
    const result: ActivityEntry[] = [];
    for (const project of projects) {
      for (const board of project.boards) {
        for (const task of board.tasks) {
          result.push(
            ...collectTaskActivities(
              task,
              board.columns,
              {
                projectId: project.project_id,
                projectName: project.code_name,
              },
              base,
            ),
          );
        }
      }
    }
    return result;
  }, [projects]);

  return { entries, projects, isLoading, error };
}
